import React, { useMemo, useRef } from 'react';

import type { CartItemType } from '@zola-helpers/client/dist/es/@types/cart';
import type { ConvertWebApiDeclaredToActual } from '@zola-helpers/client/dist/es/@types/svc-web-api';
import type { WProjectFullView, WProjectCustomizationFullView } from '@zola/svc-web-api-ts-client';
import {
  DigitalCanvasAdapter,
  DigitalCanvasProps,
} from '../../../../paper/cards/components/DigitalCanvas/DigitalCanvas';
import { useCartContext } from '../../CartContext';
import useContainerDimensions from '../../../../hooks/useContainerDimensions';
import { useComputedStyle } from '../../../../hooks/useComputedStyle/useComputedStyle';

/**
 * Sizes the DigitalCanvas image to fit the container.  Will not work for flex
 * containers.  In that case, pass in a width.
 */
const Customization = ({
  cardProject,
  className,
  customization,
  width,
  ...rest
}: {
  cardProject: ConvertWebApiDeclaredToActual<WProjectFullView>;
  customization: ConvertWebApiDeclaredToActual<WProjectCustomizationFullView>;
  className?: string;
} & Partial<DigitalCanvasProps>) => {
  const ref = useRef<HTMLDivElement>(null);

  const { clientWidth } = useContainerDimensions(ref);
  const { paddingLeft, paddingRight } = useComputedStyle(ref);

  // This is how wide our container is.
  // We want a 1:1 aspect ration for the container
  // for landscape cards, we'll scale the height down to fit within our 1:1 container
  // but for portraits, we have to scale the width down based on the height
  const resolvedWidth = useMemo(() => {
    return clientWidth - parseFloat(paddingLeft || '0') - parseFloat(paddingRight || '0');
  }, [clientWidth, paddingLeft, paddingRight]);

  const scaledWidth = useMemo(() => {
    const page = customization.pages?.[0];
    if (!page) {
      return resolvedWidth;
    }
    if ((page.orientation as string | undefined) === 'PORTRAIT') {
      // We are going for a 1:1 aspect ratio which is why we can divide height by width
      let { width } = page;
      const { height } = page;

      if (page.folded) {
        width /= 2;
      }

      const ratio = width / height;
      return resolvedWidth * ratio;
    }
    return resolvedWidth;
  }, [customization.pages, resolvedWidth]);

  return (
    <div className={className} ref={ref}>
      <DigitalCanvasAdapter
        cardProject={cardProject}
        customization={customization}
        width={width || scaledWidth}
        disableTranslationScale
        disableBleed
        alignItems="flex-start"
        justifyContent="flex-start"
        aspectRatioValue="initial"
        {...rest}
      />
    </div>
  );
};

export const CardItemImage = ({
  item,
  ...rest
}: { item: CartItemType; className?: string } & Partial<DigitalCanvasProps>) => {
  const { getCardCustomization, getCardProject } = useCartContext();

  const cardProject = useMemo(
    () => getCardProject(item.projectUuid as string),
    [getCardProject, item.projectUuid]
  );

  const customization = useMemo(() => {
    return getCardCustomization(item.projectUuid as string, item.customizationUuid as string);
  }, [getCardCustomization, item.customizationUuid, item.projectUuid]);

  if (!customization || !cardProject) {
    return null;
  }

  return <Customization cardProject={cardProject} customization={customization} {...rest} />;
};
